import examClient from "../../../utils/examClient";

/**
 * publicAdapter — ExamRunner over the anonymous /public/exams endpoints.
 *
 * The public API was designed for this runner, so it needs almost no mapping:
 * it already returns { attempt, questions, exam, policy } with a
 * server-computed `timeLeftSeconds` and a policy shape the runner reads
 * directly.
 *
 * The one thing worth noticing is what is NOT here: no fallback, no local
 * scoring, no client-side "is my time up" decision. Every one of those answers
 * comes from the server on every call.
 */
export default function createPublicAdapter({ slug, candidateName }) {
  const base = `/public/exams/${slug}`;

  const normalize = (data) => ({
    attempt: data.attempt,
    questions: data.questions || [],
    passages: data.passages || [],
    exam: {
      ...(data.exam || {}),
      candidateName,
      // The watermark carries the candidate's own identifier, so a photograph
      // of the screen taken with a second device is traceable back to them.
      watermarkText: candidateName || "",
    },
    policy: data.policy || {},
  });

  return {
    async loadAttempt() {
      // Start is idempotent: an existing in-progress attempt resumes with the
      // SAME paper and the SAME deadline rather than starting a new one, so the
      // runner can call this on every mount without special-casing a reload.
      try {
        const { data } = await examClient.post(`${base}/attempt/start`);
        return normalize(data);
      } catch (err) {
        // 409 means "already submitted" — a resume attempt on a finished exam.
        // Anything else is genuinely fatal and must reach the runner.
        if (err?.response?.status !== 409) throw err;
        const { data } = await examClient.get(`${base}/attempt`);
        return normalize(data);
      }
    },

    async saveAnswer(questionId, patch) {
      const { data } = await examClient.put(`${base}/attempt/answer`, { questionId, ...patch });
      return data;
    },

    async reportEvent(type, meta = {}) {
      const { clientEventId, clientAt, ...rest } = meta;
      const { data } = await examClient.post(`${base}/attempt/event`, {
        type, meta: rest, clientEventId, clientAt,
      });
      return data;
    },

    async heartbeat() {
      const { data } = await examClient.post(`${base}/attempt/heartbeat`);
      return data;
    },

    /** A short-lived link to a passage's PDF, minted per request. */
    async passageFileUrl(passageId) {
      const { data } = await examClient.get(`${base}/attempt/passages/${passageId}/file`);
      return data?.url || null;
    },

    async submit({ timeSpent }) {
      const { data } = await examClient.post(`${base}/attempt/submit`, { timeSpent });
      return data;
    },

    /** Close the open timed section and open the next. Idempotent server-side. */
    async nextSection(from) {
      const { data } = await examClient.post(`${base}/attempt/section/next`, { from });
      return data;
    },

    /** Run code against a coding problem's sample tests, or the candidate's own input. */
    async runCode(questionId, body) {
      const { data } = await examClient.post(`${base}/attempt/code/run`, { questionId, ...body });
      return data;
    },

    /** Proctoring frame. Multipart — never base64, which the JSON limit rejects. */
    async uploadShot(blob, { reason = "interval", width, height } = {}) {
      const form = new FormData();
      form.append("image", blob, "frame.jpg");
      form.append("reason", reason);
      if (width) form.append("width", String(width));
      if (height) form.append("height", String(height));
      const { data } = await examClient.post(`${base}/attempt/shot`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },

    async reportCameraLost(reason) {
      const { data } = await examClient.post(`${base}/attempt/camera-lost`, { reason });
      return data;
    },
  };
}
